// Feature: MCRO001-F0037 (inherits MCRO001-F0007/F0027/F0036)
// Test: T-MCRO-037

import React, { useCallback, useRef } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const SWIPE_ARM_THRESHOLD_PX = 24;

// MCRO001-F0037 cases G/H: a rehearsal room is a propped-up tablet, so a horizontal
// drag across the video seeks. It resolves to one target count and calls the same
// `onSeek` count-setting path Previous/Next and the MCRO001-F0036 scrub slider use —
// it never reads, derives, buffers or advances a media position itself.
//
// It is `aria-hidden` on purpose: every destination it can reach is already reachable
// from the accessible scrub slider and the keyboard shuttle, so exposing a second
// pointer-only widget to assistive technology would add noise, not capability.
// Arming requires a deliberate, predominantly horizontal movement, so a tap on the
// video or a vertical page scroll never moves the count.
export default function RehearsalStageSwipe({ min, max, count, onSeek, threshold = SWIPE_ARM_THRESHOLD_PX }) {
  const dragRef = useRef(null);

  const end = useCallback(() => { dragRef.current = null; }, []);

  const pointerDown = useCallback((event) => {
    if (typeof onSeek !== 'function') return;
    const rect = event.currentTarget.getBoundingClientRect?.();
    if (!rect || rect.width <= 0) return;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) { /* capture is optional */ }
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: rect.width,
      startCount: Number(count),
      armed: false,
    };
  }, [count, onSeek]);

  const pointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || typeof onSeek !== 'function') return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.armed) {
      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;
      drag.armed = true;
    }
    event.preventDefault?.();
    const bounds = { min: Math.min(min, max), max: Math.max(min, max) };
    const span = Math.max(1, bounds.max - bounds.min);
    const base = Number.isFinite(drag.startCount) ? drag.startCount : bounds.min;
    const target = Math.round(base + (dx / drag.width) * span);
    onSeek(clamp(target, bounds.min, bounds.max));
  }, [max, min, onSeek, threshold]);

  return <div
    className="mc-stage-swipe"
    aria-hidden="true"
    onPointerDown={pointerDown}
    onPointerMove={pointerMove}
    onPointerUp={end}
    onPointerCancel={end}
    onLostPointerCapture={end}
  />;
}
